import { useMemo } from 'react';

import { Box, ButtonBase, Divider } from '@mui/material';
import { DateRangeIcon } from '@mui/x-date-pickers';
import { format, isValid } from 'date-fns';
import { useNavigate } from 'react-router';

import { DataHubCard } from '@/features/DataHub/components/DataHubCard';
import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import { MIN_DATE } from '@/features/DataHub/modules/Export/constants/dateSettings';
import { useExportSettings } from '@/features/DataHub/modules/Export/context/exportContexts';
import { DateRangeType, ExportTypes } from '@/features/DataHub/modules/Export/types';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { closeDrawer } from '@/slices/drawerSlice';

import DateRangeSettingsFields from './DateRangeSettingsFields';

const periodLabels: Record<string, string> = {
  d: 'Days',
  m: 'Months',
  y: 'Years'
};

const DateSettings = () => {
  const navigate = useNavigate();
  const { exportSettings } = useExportSettings();

  const exportType = useAppSelector(state => state.export.type);

  const { timezoneLabel, dateFormatValue } = useDataHubUserConfig();

  const dispatch = useAppDispatch();

  const collapsedSummary = useMemo(() => {
    const tz = `Timezone: ${timezoneLabel}`;
    switch (exportSettings.dateRangeType) {
      case DateRangeType.ALL:
        return `All, ${tz}`;
      case DateRangeType.RANGE:
        return `Last ${exportSettings.periodNumber} ${periodLabels[exportSettings.period] ?? exportSettings.period}, ${tz}`;
      case DateRangeType.DATES: {
        const from =
          exportSettings.from && isValid(exportSettings.from)
            ? format(exportSettings.from, dateFormatValue)
            : '';
        const to =
          exportSettings.to && isValid(exportSettings.to)
            ? format(exportSettings.to, dateFormatValue)
            : '';
        return `${from} - ${to}, ${tz}`;
      }
      default:
        return '';
    }
  }, [
    exportSettings.dateRangeType,
    exportSettings.periodNumber,
    exportSettings.period,
    exportSettings.from,
    exportSettings.to,
    timezoneLabel,
    dateFormatValue
  ]);

  if (
    exportSettings.exportType === ExportTypes.IMAGE &&
    (exportType === 'seasonal' || exportType === 'forwardSimple')
  ) {
    return null;
  }
  return (
    <DataHubCard
      expandable
      collapsedSummary={collapsedSummary}
      icon={
        <DateRangeIcon
          sx={{
            fontSize: '16px'
          }}
        />
      }
      subtitle='Select date range and timezone options'
      title='Date settings'
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          fontSize: '14px',
          my: 1.5
        }}
      >
        <Divider sx={{ mb: 2 }} />
        <DateRangeSettingsFields
          dateFormat={dateFormatValue}
          maxDate={new Date()}
          minDate={MIN_DATE}
        />
        <Divider sx={{ my: 2 }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline'
          }}
        >
          Timezone: {timezoneLabel}{' '}
          <ButtonBase
            sx={{
              ml: 2,
              color: 'primary.main'
            }}
            onClick={() => {
              navigate('/profile');
              dispatch(closeDrawer());
            }}
          >
            Change in Settings
          </ButtonBase>
        </Box>
        <Divider sx={{ mt: 2 }} />
      </Box>
    </DataHubCard>
  );
};

export default DateSettings;
